import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, Crown, Clock, AlertTriangle, Receipt, CalendarClock, CreditCard, Download, Gauge, ArrowRight, Sparkles, PartyPopper } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { PageHeader } from "@/pages/Dashboard";
import { useAuth } from "@/context/AuthContext";
import { usePlan } from "@/context/PlanContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UpgradeNudge } from "@/components/UpgradeIntelligence";
import { toast } from "sonner";

const CANCEL_REASONS = [
  ["too_expensive", "Too expensive"], ["not_using", "Not using it enough"],
  ["missing_features", "Missing features"], ["technical", "Technical problems"],
  ["switching", "Switching services"], ["business_closed", "Business closed"],
  ["financial", "Temporary financial issue"], ["other", "Other"],
];

const fmtBytes = (b) => {
  if (b == null) return "∞";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

const PLAN_ORDER = ["starter", "growth", "enterprise"];
const rank = (id) => PLAN_ORDER.indexOf(id);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; } };

function InfoChip({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Icon className="w-4 h-4 text-[#D4AF37] shrink-0" />
      <span className="text-[#71717A] text-xs">{label}:</span>
      <span className="text-[#D4D4D8] text-xs font-medium">{value}</span>
    </div>
  );
}

function StatusBanner({ sub, extra, onCancel, canceling, onReactivate, reactivating, onUpdatePayment, onRetry, paying, onPortal }) {
  const status = sub?.status;
  const tier = sub?.tier;
  const nextBilling = extra?.subscription?.next_billing_date;
  const pm = extra?.payment_method;
  if (status === "past_due") {
    const readOnly = sub?.read_only;
    const graceDays = sub?.grace_days_remaining;
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 mb-8" data-testid="past-due-banner">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0"><AlertTriangle className="w-6 h-6 text-red-400" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-red-400 text-xs uppercase tracking-wider">Payment failed · {tier}</div>
            <div className="font-heading text-2xl text-white" data-testid="past-due-status">
              {readOnly ? "Account is read-only" : `${graceDays} day${graceDays !== 1 ? "s" : ""} left in grace period`}
            </div>
            <div className="text-sm text-[#A1A1AA]">
              {readOnly
                ? "Your grace period has ended. Update your payment method to restore full access."
                : "We couldn't process your payment. Update your payment method to keep full access — your data is always safe."}
            </div>
            <p className="text-xs text-[#71717A] mt-2" data-testid="past-due-reassurance">
              You can always sign in, cancel your subscription, download your data & invoices, and contact support — even in read-only mode. Your data is never deleted.
            </p>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <Button disabled={paying} onClick={onUpdatePayment} data-testid="update-payment-method-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90">
              {paying ? "Redirecting…" : "Update payment method"}</Button>
            <Button variant="outline" disabled={paying} onClick={onRetry} data-testid="retry-payment-btn"
              className="border-white/15 bg-transparent text-[#A1A1AA] hover:text-white hover:bg-white/5 rounded-full">
              Retry payment</Button>
            <Button variant="outline" disabled={canceling} onClick={onCancel} data-testid="cancel-subscription-btn"
              className="border-white/15 bg-transparent text-[#A1A1AA] hover:text-red-400 hover:border-red-400/30 rounded-full">
              {canceling ? "Canceling…" : "Cancel subscription"}</Button>
          </div>
        </div>
      </div>
    );
  }
  if (status === "trialing") {
    return (
      <div className="rounded-2xl border border-[#D4AF37]/30 bg-[#151310] p-6 mb-8" data-testid="trial-banner">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><Clock className="w-6 h-6 text-[#0A0A0B]" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[#D4AF37] text-xs uppercase tracking-wider">Free trial · {tier}</div>
            <div className="font-heading text-2xl text-white" data-testid="trial-countdown">{sub.days_remaining} day{sub.days_remaining !== 1 ? "s" : ""} left</div>
            <div className="text-sm text-[#A1A1AA]" data-testid="subscription-status">
              {sub?.cancel_at_period_end
                ? "Cancels at period end — you keep access until your trial ends, then the account becomes read-only."
                : "Subscribe before it ends to keep full access. Your data stays safe either way."}
            </div>
          </div>
          {sub?.cancel_at_period_end ? (
            <Button disabled={reactivating} onClick={onReactivate} data-testid="resume-subscription-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full shrink-0 hover:opacity-90">
              {reactivating ? "Resuming…" : "Resume subscription"}</Button>
          ) : (
            <Button variant="outline" disabled={canceling} onClick={onCancel} data-testid="cancel-trial-btn"
              className="border-white/15 bg-transparent text-[#A1A1AA] hover:text-white hover:bg-white/5 rounded-full shrink-0">
              {canceling ? "Canceling…" : "Cancel trial"}</Button>
          )}
        </div>
        {nextBilling && (
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 pt-4 border-t border-white/8">
            <InfoChip icon={CalendarClock} label={sub?.cancel_at_period_end ? "Access until" : "Trial ends"}
              value={sub?.cancel_at_period_end && sub?.cancels_at ? `${fmtDate(sub.cancels_at)} (${sub?.access_ends_in_days}d left)` : fmtDate(nextBilling)}
              testid={sub?.cancel_at_period_end ? "cancels-at-date" : "trial-end-date"} />
            <InfoChip icon={CreditCard} label="Payment method" value={pm?.description || "None"} testid="payment-method" />
          </div>
        )}
      </div>
    );
  }
  if (status === "expired" || status === "canceled") {
    const isCanceled = status === "canceled";
    return (
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 mb-8 flex items-center gap-4 flex-wrap" data-testid={isCanceled ? "subscription-canceled-banner" : "trial-expired-banner"}>
        <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0"><AlertTriangle className="w-6 h-6 text-amber-400" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-amber-400 text-xs uppercase tracking-wider">{isCanceled ? "Subscription ended · read-only" : "Trial ended"}</div>
          <div className="font-heading text-2xl text-white">Reactivate to continue</div>
          <div className="text-sm text-[#A1A1AA]">Your account is read-only. All your data, documents, progress, and completed courses are preserved — {tier ? "reactivate your plan" : "pick a plan"} to unlock full access again.</div>
          <p className="text-xs text-[#71717A] mt-2" data-testid="canceled-reassurance">
            You can always sign in, download your data & invoices, and contact support. Nothing is ever deleted while your account is read-only.
          </p>
        </div>
        {tier && (
          <div className="flex gap-2 shrink-0">
            <Button disabled={reactivating} onClick={onReactivate} data-testid="reactivate-subscription-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90">
              {reactivating ? "Reactivating…" : `Reactivate ${tier} plan`}</Button>
            <Button variant="outline" disabled={paying} onClick={onPortal} data-testid="manage-billing-btn"
              className="border-white/15 bg-transparent text-[#A1A1AA] hover:text-white hover:bg-white/5 rounded-full">
              {paying ? "Opening…" : "View invoices"}</Button>
          </div>
        )}
      </div>
    );
  }
  const active = status === "active" || status === "trialing";
  const scheduledCancel = sub?.cancel_at_period_end;
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 mb-8" data-testid="current-plan">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><Crown className="w-6 h-6 text-[#0A0A0B]" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[#71717A] text-xs uppercase tracking-wider">Current plan</div>
          <div className="font-heading text-2xl text-white capitalize" data-testid="subscription-status">
            {status === "active" ? tier : status === "trialing" ? `${tier} (trial)` : "Free"} <span className="text-sm text-[#71717A]">· {scheduledCancel ? "Cancels at period end" : status === "active" ? "Active" : status === "trialing" ? "Trialing" : "No active subscription"}</span>
          </div>
        </div>
        {active && (scheduledCancel ? (
          <Button disabled={reactivating} onClick={onReactivate} data-testid="resume-subscription-btn"
            className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full shrink-0 hover:opacity-90">
            {reactivating ? "Resuming…" : "Resume subscription"}</Button>
        ) : (
          <div className="flex gap-2 shrink-0 flex-wrap">
            <Button disabled={paying} onClick={onPortal} data-testid="manage-billing-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90">
              {paying ? "Opening…" : "Manage billing"}</Button>
            <Button variant="outline" disabled={canceling} onClick={onCancel} data-testid={status === "trialing" ? "cancel-trial-btn" : "cancel-subscription-btn"}
              className="border-white/15 bg-transparent text-[#A1A1AA] hover:text-red-400 hover:border-red-400/30 rounded-full">
              {canceling ? "Canceling…" : status === "trialing" ? "Cancel trial" : "Cancel subscription"}</Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 pt-4 border-t border-white/8">
        {scheduledCancel ? (
          <InfoChip icon={CalendarClock} label="Access until" value={sub?.cancels_at ? `${fmtDate(sub.cancels_at)} (${sub?.access_ends_in_days}d left)` : "period end"} testid="cancels-at-date" />
        ) : (
          <InfoChip icon={CalendarClock} label="Next billing date" value={nextBilling ? fmtDate(nextBilling) : "—"} testid="next-billing-date" />
        )}
        <InfoChip icon={CreditCard} label="Payment method" value={pm?.description || "—"} testid="payment-method" />
      </div>
    </div>
  );
}

function PlanCard({ plan, sub, busy, cycle, onSubscribe, onTrial, onChangePlan, recommended, recReason, onRecAccept }) {
  const featured = plan.id === "growth";
  const status = sub?.status;
  const isCurrentActive = status === "active" && sub?.tier === plan.id;
  const isTrialCurrent = status === "trialing" && sub?.tier === plan.id;
  const canTrial = !["active", "trialing", "expired", "canceled"].includes(status);
  const annual = cycle === "annual";

  let label = "Subscribe";
  let action = () => onSubscribe(plan.id, cycle);
  let disabled = busy;
  let testid = `subscribe-${plan.id}`;
  if (isCurrentActive) { label = "Current plan"; disabled = true; }
  else if (isTrialCurrent) { label = busy ? "Redirecting…" : "Subscribe now"; }
  else if (status === "active") {
    if (rank(plan.id) > rank(sub.tier)) {
      label = busy ? "Updating…" : "Upgrade"; testid = `upgrade-${plan.id}`;
      // Existing Stripe subscribers upgrade in place (prorated); others go through checkout.
      if (sub?.stripe_subscription_id) action = () => onChangePlan(plan.id);
    }
    else { label = busy ? "Updating…" : "Downgrade"; action = () => onChangePlan(plan.id); testid = `downgrade-${plan.id}`; }
  }
  else if (canTrial) { label = "Start Your Free 7-Day Trial"; action = () => onTrial(plan.id); testid = `trial-${plan.id}`; }
  else { label = busy ? "Redirecting…" : "Subscribe"; }

  const wrappedAction = () => { if (recommended && !disabled) onRecAccept?.(plan.id); action(); };
  const showRecommended = recommended && !isCurrentActive;

  return (
    <div data-testid={`plan-${plan.id}`}
      className={`relative rounded-2xl p-7 border ${showRecommended ? "border-[#D4AF37]/60 bg-[#151310] shadow-[0_0_0_1px_rgba(212,175,55,0.25)]" : featured ? "border-[#D4AF37]/40 bg-[#151310]" : "border-white/8 bg-[#121214]"}`}>
      {showRecommended
        ? <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full fv-gold-gradient text-[#0A0A0B] text-[10px] font-bold uppercase tracking-wide flex items-center gap-1" data-testid={`plan-recommended-badge-${plan.id}`}><Sparkles className="w-3 h-3" /> Recommended for you</div>
        : featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full fv-gold-gradient text-[#0A0A0B] text-[10px] font-bold uppercase tracking-wide">Popular</div>}
      <h3 className="font-heading text-2xl text-white mb-1">{plan.name}</h3>
      <p className="text-[#71717A] text-sm mb-1">{plan.tagline}</p>
      {plan.best_for && <p className="text-[11px] text-[#D4AF37] mb-3" data-testid={`plan-bestfor-${plan.id}`}>Best for: {plan.best_for}</p>}
      {showRecommended && recReason && (
        <div className="mb-3 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-3 py-2 text-[11px] text-[#D4D4D8] leading-relaxed" data-testid={`plan-rec-reason-${plan.id}`}>
          {recReason}
        </div>
      )}
      <div className="mb-1">
        <span className="font-heading text-4xl fv-gold-text" data-testid={`plan-price-${plan.id}`}>
          ${annual ? plan.price_annual : plan.price}
        </span>
        <span className="text-[#71717A]">/{annual ? "yr" : "mo"}</span>
      </div>
      {annual && (plan.price * 12 - plan.price_annual) > 0 && (
        <div className="mb-5 text-[11px] font-semibold text-[#D4AF37]" data-testid={`plan-savings-${plan.id}`}>
          Save ${(plan.price * 12 - plan.price_annual).toFixed(2)}/yr · 2 months free
        </div>
      )}
      {!annual && <div className="mb-5" />}
      <ul className="space-y-2.5 mb-7">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-[#D4D4D8]"><Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />{f}</li>
        ))}
      </ul>
      <Button disabled={disabled} onClick={wrappedAction} data-testid={testid}
        className={`w-full rounded-full h-11 ${(showRecommended || featured) ? "fv-gold-gradient text-[#0A0A0B] font-semibold hover:opacity-90" : "bg-white/5 border border-white/15 text-white hover:bg-white/10"}`}>
        {label}
      </Button>
      {canTrial && <p className="text-center text-[11px] text-[#71717A] mt-3">No credit card required</p>}
    </div>
  );
}

function CycleToggle({ cycle, setCycle }) {
  return (
    <div className="flex items-center justify-center mb-8" data-testid="billing-cycle-toggle">
      <div className="inline-flex items-center rounded-full border border-white/10 bg-[#121214] p-1">
        {["monthly", "annual"].map((c) => (
          <button key={c} onClick={() => setCycle(c)} data-testid={`cycle-${c}`}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${cycle === c ? "fv-gold-gradient text-[#0A0A0B]" : "text-[#A1A1AA] hover:text-white"}`}>
            {c === "monthly" ? "Monthly" : "Annual"}
          </button>
        ))}
      </div>
      <span className="ml-3 text-xs font-semibold text-[#D4AF37]">Save 2 months with annual</span>
    </div>
  );
}

function BillingHistory({ history, onInvoice }) {
  return (
    <div className="mt-10" data-testid="billing-history">
      <h3 className="font-heading text-xl text-white mb-3 flex items-center gap-2"><Receipt className="w-5 h-5 text-[#D4AF37]" /> Billing history</h3>
      {history.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 bg-[#0f0f11] p-8 text-center text-[#71717A]" data-testid="billing-history-empty">No transactions yet.</div>
      ) : (
        <div className="rounded-2xl border border-white/8 bg-[#121214] overflow-hidden">
          {history.map((t) => (
            <div key={t.session_id} className="flex items-center justify-between px-5 py-3.5 border-b border-white/5 last:border-0 gap-3" data-testid="billing-history-row">
              <div className="min-w-0">
                <div className="text-white text-sm capitalize">{t.plan_id ? `${t.plan_id} plan` : "Payment"}{t.kind === "downgrade" ? " (downgrade)" : ""}</div>
                <div className="text-xs text-[#71717A]">{fmtDate(t.created_at)}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-white text-sm">${Number(t.amount).toFixed(2)}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${
                  t.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-400"
                  : t.status === "plan_changed" ? "bg-white/10 text-[#A1A1AA]"
                  : "bg-amber-500/15 text-amber-400"}`}>
                  {t.payment_status === "paid" ? "Paid" : t.status === "plan_changed" ? "Changed" : (t.payment_status || t.status)}</span>
                <button onClick={() => onInvoice(t.session_id)} data-testid={`invoice-download-${t.session_id}`}
                  title="Download invoice"
                  className="inline-flex items-center gap-1 text-xs text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">
                  <Download className="w-4 h-4" /> Invoice
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UsageSection({ usage }) {
  if (!usage) return null;
  return (
    <div className="mb-10" data-testid="usage-section">
      <h3 className="font-heading text-xl text-white mb-3 flex items-center gap-2"><Gauge className="w-5 h-5 text-[#D4AF37]" /> Plan usage this month</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {usage.items.map((it) => {
          const isBytes = it.is_bytes;
          const usedLabel = isBytes ? fmtBytes(it.used) : it.used;
          const limitLabel = it.unlimited ? "∞" : (isBytes ? fmtBytes(it.limit) : it.limit);
          const pct = it.unlimited || !it.limit ? 0 : Math.min(100, Math.round((it.used / it.limit) * 100));
          const near = pct >= 80;
          return (
            <div key={it.metric} className="rounded-xl border border-white/8 bg-[#121214] px-4 py-3" data-testid={`usage-${it.metric}`}>
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-sm text-[#D4D4D8]">{it.label}</span>
                <span className={`text-xs ${near ? "text-amber-400" : "text-[#71717A]"}`}>{usedLabel} / {limitLabel}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                <div className={`h-full rounded-full ${near ? "bg-amber-400" : "fv-gold-gradient"}`} style={{ width: `${it.unlimited ? 4 : pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      {usage.reset_date && <p className="text-xs text-[#52525B] mt-3">Monthly counters reset on {fmtDate(usage.reset_date)}.</p>}
    </div>
  );
}

function CancelModal({ open, onOpenChange, onConfirm, canceling, onSwitchToLower }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [phase, setPhase] = useState("save"); // save (intervention) -> confirm
  const [iv, setIv] = useState(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (open) {
      setPhase("save"); setReason(""); setNote(""); setIv(null);
      api.get("/retention/cancel-intervention").then(({ data }) => setIv(data)).catch(() => setPhase("confirm"));
    }
  }, [open]);

  const keep = (outcome) => {
    api.post("/retention/cancel-intervention/accept", { outcome }).catch(() => {});
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 text-white max-h-[85vh] overflow-y-auto" data-testid="cancel-modal">
        {phase === "save" && iv ? (
          <>
            <DialogHeader><DialogTitle>Before you go — here's what you've built</DialogTitle>
              <DialogDescription className="text-[#71717A]">A quick look at your progress. You can continue cancelling anytime.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-2 my-1" data-testid="cancel-intervention">
              <div className="rounded-xl border border-white/10 bg-[#0d0d0f] p-3 text-center">
                <div className="font-heading text-2xl fv-gold-text">{iv.progress?.completion_pct ?? 0}%</div>
                <div className="text-[10px] text-[#71717A]">Journey complete</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-[#0d0d0f] p-3 text-center">
                <div className="font-heading text-2xl text-white">{iv.assets?.documents ?? 0}</div>
                <div className="text-[10px] text-[#71717A]">Documents</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-[#0d0d0f] p-3 text-center">
                <div className="font-heading text-2xl text-white">{iv.assets?.contracts ?? 0}</div>
                <div className="text-[10px] text-[#71717A]">Contracts</div>
              </div>
            </div>
            {iv.unfinished?.title && (
              <div className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-3 my-1" data-testid="cancel-unfinished">
                <div className="text-[10px] uppercase tracking-wide text-[#D4AF37] mb-1">One useful thing left to do</div>
                <div className="text-sm text-white font-medium">{iv.unfinished.title}</div>
                {iv.unfinished.why && <p className="text-[11px] text-[#A1A1AA] mt-1">{iv.unfinished.why}</p>}
              </div>
            )}
            <p className="text-xs text-[#71717A]" data-testid="cancel-access-change">{iv.access_change}</p>
            <DialogFooter className="gap-2 flex-wrap">
              {iv.lower_tier?.available && (
                <Button disabled={switching} data-testid="cancel-switch-lower"
                  onClick={async () => { setSwitching(true); keep("downgraded"); await onSwitchToLower(iv.lower_tier.plan); setSwitching(false); onOpenChange(false); }}
                  className="bg-white/5 border border-white/15 text-white hover:bg-white/10 rounded-full">
                  {switching ? "Switching…" : `Switch to ${iv.lower_tier.name} ($${iv.lower_tier.price}/mo)`}</Button>
              )}
              <Button variant="outline" onClick={() => { keep("kept"); onOpenChange(false); }} data-testid="cancel-keep-plan"
                className="border-white/15 bg-transparent text-[#A1A1AA] hover:text-white rounded-full">Keep my plan</Button>
              <Button onClick={() => setPhase("confirm")} data-testid="cancel-continue"
                className="bg-red-500/90 hover:bg-red-500 text-white rounded-full">Continue to cancel</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader><DialogTitle>Cancel subscription</DialogTitle>
              <DialogDescription className="text-[#71717A]">Review what happens before you cancel.</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-[#A1A1AA]">You'll keep full access until the end of your paid period, then the account becomes read-only. <span className="text-[#D4D4D8]">All your data and work are preserved</span> and you can reactivate anytime.</p>
            <div className="space-y-2 my-2">
              <div className="text-xs text-[#71717A]">Help us improve — why are you canceling? (optional)</div>
              <div className="grid grid-cols-2 gap-2">
                {CANCEL_REASONS.map(([k, l]) => (
                  <button key={k} onClick={() => setReason(k)} data-testid={`cancel-reason-${k}`}
                    className={`text-xs px-3 py-2 rounded-lg border text-left ${reason === k ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-[#A1A1AA] hover:text-white"}`}>{l}</button>
                ))}
              </div>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything else? (optional)"
                data-testid="cancel-note" className="bg-[#0d0d0f] border-white/10 text-white text-sm" />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-modal-keep"
                className="border-white/15 bg-transparent text-[#A1A1AA] hover:text-white rounded-full">Keep my plan</Button>
              <Button disabled={canceling} onClick={() => onConfirm(reason, note)} data-testid="cancel-modal-confirm"
                className="bg-red-500/90 hover:bg-red-500 text-white rounded-full">{canceling ? "Canceling…" : "Confirm cancellation"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DowngradeModal({ preview, onOpenChange, onConfirm, busy }) {
  if (!preview) return null;
  const isUpgrade = preview.direction === "upgrade";
  return (
    <Dialog open={!!preview} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 text-white" data-testid={isUpgrade ? "upgrade-modal" : "downgrade-modal"}>
        <DialogHeader><DialogTitle className="capitalize">{isUpgrade ? "Upgrade" : "Downgrade"} to {preview.target_plan}</DialogTitle>
          <DialogDescription className="text-[#71717A]">Review the changes before confirming.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-[#A1A1AA]">New price <span className="text-white font-medium">${preview.new_price}/mo</span>. This takes effect <span className="text-[#D4D4D8]">{preview.effective}</span>.</p>
        {isUpgrade && preview.features_gained?.length > 0 && (
          <div className="my-2">
            <div className="text-xs uppercase tracking-wide text-[#D4AF37] mb-2">You'll unlock</div>
            <ul className="space-y-1.5">
              {preview.features_gained.map((f) => <li key={f} className="text-sm text-[#D4D4D8] flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" /> {f}</li>)}
            </ul>
          </div>
        )}
        {!isUpgrade && preview.features_lost?.length > 0 && (
          <div className="my-2">
            <div className="text-xs uppercase tracking-wide text-amber-400 mb-2">You'll lose access to</div>
            <ul className="space-y-1.5">
              {preview.features_lost.map((f) => <li key={f} className="text-sm text-[#D4D4D8] flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" /> {f}</li>)}
            </ul>
          </div>
        )}
        <p className="text-xs text-[#71717A]">{preview.note}</p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="downgrade-cancel"
            className="border-white/15 bg-transparent text-[#A1A1AA] hover:text-white rounded-full">Keep current plan</Button>
          <Button disabled={busy} onClick={() => onConfirm(preview.target_plan)} data-testid={isUpgrade ? "upgrade-confirm" : "downgrade-confirm"}
            className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">{busy ? "Updating…" : `Confirm ${isUpgrade ? "upgrade" : "downgrade"}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CheckoutConfirmModal({ state, onOpenChange, onConfirm, busy }) {
  if (!state?.plan) return null;
  const { plan, cycle } = state;
  const annual = cycle === "annual";
  const price = annual ? plan.price_annual : plan.price;
  return (
    <Dialog open={!!state} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-[#D4AF37]/25 text-white" data-testid="checkout-confirm-modal">
        <DialogHeader><DialogTitle>Confirm your {plan.name} plan</DialogTitle>
          <DialogDescription className="text-[#71717A]">Review your selection before continuing to secure checkout.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-white/10 bg-[#0d0d0f] p-4 my-1">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-white font-medium">{plan.name} · {annual ? "Annual" : "Monthly"}</span>
            <span><span className="text-2xl font-heading fv-gold-text" data-testid="checkout-confirm-price">${price}</span><span className="text-xs text-[#71717A]">/{annual ? "yr" : "mo"}</span></span>
          </div>
          {annual && (plan.price * 12 - plan.price_annual) > 0 && <div className="text-[11px] font-semibold text-[#D4AF37] mb-1">You save ${(plan.price * 12 - plan.price_annual).toFixed(2)}/yr · 2 months free</div>}
          <ul className="space-y-1.5 mt-3">
            {(plan.features || []).slice(0, 5).map((f) => <li key={f} className="flex items-start gap-2 text-xs text-[#D4D4D8]"><Check className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />{f}</li>)}
          </ul>
        </div>
        <p className="text-xs text-[#71717A]">Your plan unlocks the moment payment is confirmed by Stripe. Cancel anytime — you keep access until the end of your paid period, and your data is always preserved.</p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="checkout-confirm-cancel" className="border-white/15 bg-transparent text-[#A1A1AA] hover:text-white rounded-full">Back</Button>
          <Button disabled={busy} onClick={onConfirm} data-testid="checkout-confirm-continue" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">{busy ? "Redirecting…" : "Continue to secure checkout"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaidSuccessModal({ info, onClose, onGo }) {
  if (!info) return null;
  return (
    <Dialog open={!!info} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121214] border-[#D4AF37]/30 text-white" data-testid="paid-success-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PartyPopper className="w-5 h-5 text-[#D4AF37]" /> You're all set!</DialogTitle>
          <DialogDescription className="text-[#71717A]">Your payment is confirmed and your plan is active.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-white/10 bg-[#0d0d0f] p-4 my-1" data-testid="paid-success-summary">
          <div className="flex items-baseline justify-between">
            <span className="text-white font-medium capitalize">{info.tier} plan</span>
            <span className="text-sm text-[#A1A1AA]">{info.cycle === "annual" ? "Annual" : "Monthly"}{info.price ? ` · $${info.price}/${info.cycle === "annual" ? "yr" : "mo"}` : ""}</span>
          </div>
        </div>
        {info.action && (
          <div className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-4" data-testid="paid-success-first-action">
            <div className="text-[11px] uppercase tracking-wide text-[#D4AF37] mb-1">Recommended first step</div>
            <div className="text-white text-sm font-medium">{info.action.title}</div>
            {info.action.why && <p className="text-xs text-[#A1A1AA] mt-1">{info.action.why}</p>}
            <Button onClick={() => onGo(info.action.path)} data-testid="paid-success-action-btn" className="mt-3 fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-9 text-sm">{info.action.label} <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="paid-success-manage" className="border-white/15 bg-transparent text-[#A1A1AA] hover:text-white rounded-full">Stay on billing</Button>
          <Button onClick={() => onGo("/dashboard")} data-testid="paid-success-dashboard" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">Go to dashboard</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Billing() {
  const { user, refresh } = useAuth();
  const { refresh: refreshPlan } = usePlan();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [paying, setPaying] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [history, setHistory] = useState([]);
  const [billingInfo, setBillingInfo] = useState(null);
  const [usage, setUsage] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [downgradePreview, setDowngradePreview] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [recommend, setRecommend] = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);
  const [params, setParams] = useSearchParams();
  const billingViewed = useRef(false);
  const cancelTracked = useRef(false);

  const track = useCallback((event, props) => {
    api.post("/analytics/track", { event, props }).catch(() => {});
  }, []);

  useEffect(() => {
    if (billingViewed.current) return;
    billingViewed.current = true;
    track("billing_view", {});
  }, [track]);

  // Returned from Stripe Checkout via its back/cancel link (no payment was attempted or completed).
  useEffect(() => {
    if (params.get("checkout") !== "canceled") return;
    if (!cancelTracked.current) { cancelTracked.current = true; track("checkout_canceled", {}); }
    const next = new URLSearchParams(params);
    next.delete("checkout");
    setParams(next, { replace: true });
  }, [params, setParams, track]);

  const loadHistory = useCallback(() => {
    api.get("/billing/history").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);

  const loadBillingInfo = useCallback(() => {
    api.get("/billing/subscription").then(({ data }) => setBillingInfo(data)).catch(() => {});
  }, []);

  const loadUsage = useCallback(() => {
    api.get("/subscription/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/auth/mode").then(({ data }) => setDevMode(!!data?.dev_auth_bypass)).catch(() => {});
    api.get("/subscription/recommend-plan").then(({ data }) => setRecommend(data)).catch(() => {});
    loadHistory();
    loadBillingInfo();
    loadUsage();
  }, [loadHistory, loadBillingInfo, loadUsage]);

  // Returning from a checkout-recovery email: record the truthful funnel signal + preselect.
  useEffect(() => {
    if (params.get("recovery") === "1") {
      api.post("/billing/recovery-click").catch(() => {});
      const c = params.get("cycle");
      if (c === "annual" || c === "monthly") setCycle(c);
    }
  }, [params]);

  const poll = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) { toast.error("Payment status check timed out."); return; }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        await refresh(); refreshPlan(); loadHistory(); loadBillingInfo(); loadUsage();
        // Build a truthful success summary from confirmed Stripe/subscription state.
        try {
          const [{ data: subData }, { data: action }] = await Promise.all([
            api.get("/billing/subscription"),
            api.get("/subscription/first-paid-action"),
          ]);
          const s = subData?.subscription || {};
          const tier = s.tier;
          const c = s.billing_cycle || "monthly";
          const planDef = (plans || []).find((p) => p.id === tier);
          const price = planDef ? (c === "annual" ? planDef.price_annual : planDef.price) : null;
          setSuccessInfo({ tier, cycle: c, price, action });
          api.post("/analytics/track", { event: "paid_success_viewed", props: { plan_id: tier, billing_cycle: c } }).catch(() => {});
        } catch (e) {
          toast.success("Payment successful! Your plan is now active.");
        }
        setParams({}, { replace: true });
        return;
      }
      if (data.status === "expired") { toast.error("Payment session expired."); setParams({}, { replace: true }); return; }
    } catch (err) { logError("Billing", "Payment status poll pending", err?.message); }
    setTimeout(() => poll(sessionId, attempts + 1), 2000);
  }, [refresh, refreshPlan, setParams, loadHistory, loadBillingInfo, loadUsage, plans]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) { toast.info("Confirming your payment…"); poll(sid); }
  }, [params, poll]);

  const subscribe = useCallback(async (planId, cycle = "monthly") => {
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin, billing_cycle: cycle });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setBusy("");
    }
  }, []);

  // Show a brief plan/price/what-you-get summary before redirecting to Stripe.
  const requestSubscribe = useCallback((planId, c) => {
    track("subscribe_cta_click", { plan_id: planId, billing_cycle: c, cta_location: "plan_card" });
    const plan = (plans || []).find((p) => p.id === planId);
    if (!plan) { subscribe(planId, c); return; }
    setConfirmPlan({ plan, cycle: c });
  }, [plans, subscribe, track]);

  const recAccept = useCallback((planId) => {
    api.post("/analytics/track", { event: "plan_recommendation_accepted", props: { plan_id: planId } }).catch(() => {});
  }, []);

  const startTrial = useCallback(async (planId) => {
    setBusy(planId);
    try {
      await api.post("/billing/trial", { plan_id: planId });
      await refresh(); refreshPlan(); loadBillingInfo(); loadHistory(); loadUsage();
      toast.success("Your 7-day free trial has started!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start trial");
    } finally { setBusy(""); }
  }, [refresh, refreshPlan, loadBillingInfo, loadHistory, loadUsage]);

  const changePlan = useCallback(async (planId) => {
    setBusy(planId);
    try {
      await api.post("/billing/change-plan", { plan_id: planId });
      await refresh(); refreshPlan(); loadHistory(); loadBillingInfo(); loadUsage();
      setDowngradePreview(null);
      toast.success("Your plan has been updated.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.message : formatApiError(detail) || "Could not change plan");
    } finally { setBusy(""); }
  }, [refresh, refreshPlan, loadHistory, loadBillingInfo, loadUsage]);

  const requestDowngrade = useCallback(async (planId) => {
    try {
      const { data } = await api.post("/subscription/plan-change-preview", { plan_id: planId });
      setDowngradePreview(data);
    } catch { changePlan(planId); }
  }, [changePlan]);

  const cancel = useCallback(async (reason = "", reason_note = "") => {
    setCanceling(true);
    try {
      await api.post("/billing/cancel", { reason, reason_note });
      await refresh(); refreshPlan(); loadBillingInfo(); loadHistory();
      setShowCancel(false);
      toast.success("Your subscription has been canceled.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not cancel");
    } finally { setCanceling(false); }
  }, [refresh, refreshPlan, loadBillingInfo, loadHistory]);

  const reactivate = useCallback(async () => {
    setReactivating(true);
    try {
      const { data } = await api.post("/billing/reactivate", { origin_url: window.location.origin });
      if (data?.url) { window.location.href = data.url; return; }
      await refresh(); refreshPlan(); loadBillingInfo(); loadUsage();
      toast.success("Welcome back! Your subscription is active again.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reactivate");
    } finally { setReactivating(false); }
  }, [refresh, refreshPlan, loadBillingInfo, loadUsage]);

  const updatePaymentMethod = useCallback(async () => {
    setPaying(true);
    try {
      const tier = user?.subscription?.tier || "growth";
      const { data } = await api.post("/billing/update-payment-method", { plan_id: tier, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not open payment update");
      setPaying(false);
    }
  }, [user]);

  const retryPayment = useCallback(async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/billing/retry-payment", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not retry payment");
      setPaying(false);
    }
  }, []);

  const openPortal = useCallback(async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not open the billing portal");
      setPaying(false);
    }
  }, []);

  const simulateFailure = useCallback(async () => {
    try {
      await api.post("/billing/report-failure");
      await refresh(); loadBillingInfo(); loadHistory();
      toast.info("Simulated a failed payment — your account is now in the grace period.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not simulate failure");
    }
  }, [refresh, loadBillingInfo, loadHistory]);

  const downloadInvoice = useCallback(async (sessionId) => {
    try {
      const res = await api.get(`/billing/invoice/${sessionId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${sessionId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not download invoice");
    }
  }, []);

  const sub = user?.subscription;

  return (
    <div className="fv-fade-up">
      <PageHeader eyebrow="Account" title="Subscription & Billing"
        subtitle="Start with a 7-day free trial on any plan — no credit card required. Upgrade or downgrade anytime." />
      <StatusBanner sub={sub} extra={billingInfo} onCancel={() => setShowCancel(true)} canceling={canceling}
        onReactivate={reactivate} reactivating={reactivating}
        onUpdatePayment={updatePaymentMethod} onRetry={retryPayment} paying={paying} onPortal={openPortal} />
      {devMode && sub?.status === "active" && (
        <button onClick={simulateFailure} data-testid="simulate-failure-btn"
          className="mb-6 text-xs text-[#71717A] hover:text-red-400 underline underline-offset-2">
          Dev: simulate a failed payment
        </button>
      )}
      {(sub?.status === "active" || sub?.status === "trialing") && <UsageSection usage={usage} />}
      <UpgradeNudge onUpgrade={(planId, c) => { track("subscribe_cta_click", { plan_id: planId, billing_cycle: c, cta_location: "upgrade_nudge" }); subscribe(planId, c); }} />
      <CycleToggle cycle={cycle} setCycle={setCycle} />
      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((p) => (
          <PlanCard key={p.id} plan={p} sub={sub} busy={busy === p.id} cycle={cycle}
            onSubscribe={requestSubscribe} onTrial={startTrial} onChangePlan={requestDowngrade}
            recommended={recommend?.recommended_plan === p.id}
            recReason={recommend?.recommended_plan === p.id ? recommend?.reason : ""}
            onRecAccept={recAccept} />
        ))}
      </div>
      <p className="text-xs text-[#52525B] mt-6">{devMode
        ? "Payments are processed securely by Stripe (test mode). Use card 4242 4242 4242 4242 with any future expiry and CVC."
        : "Payments are processed securely by Stripe. Your card details are never stored on our servers."}</p>
      <BillingHistory history={history} onInvoice={downloadInvoice} />
      <CancelModal open={showCancel} onOpenChange={setShowCancel} onConfirm={cancel} canceling={canceling}
        onSwitchToLower={async (planId) => {
          try {
            await api.post("/billing/change-plan", { plan_id: planId });
            toast.success("Plan switched — you kept your access.");
            refresh(); refreshPlan(); loadBillingInfo();
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not switch plan");
          }
        }} />
      <DowngradeModal preview={downgradePreview} onOpenChange={(v) => !v && setDowngradePreview(null)} onConfirm={changePlan} busy={!!busy} />
      <CheckoutConfirmModal state={confirmPlan} onOpenChange={(v) => !v && setConfirmPlan(null)}
        busy={!!busy}
        onConfirm={() => { if (confirmPlan) { subscribe(confirmPlan.plan.id, confirmPlan.cycle); } }} />
      <PaidSuccessModal info={successInfo} onClose={() => setSuccessInfo(null)}
        onGo={(path) => { setSuccessInfo(null); navigate(path || "/dashboard"); }} />
    </div>
  );
}
